<template>
  <div>
    <b-row no-gutters v-if="!visible">
      <h5 class="bypass">{{productType.name}}</h5>
      <a v-b-toggle="`collapse-section-${productType.id}`" class="ml-4 collapse-icon c-pointer">
        <font-awesome-icon class="when-opened" icon='caret-down' size="lg" />
        <font-awesome-icon class="when-closed fa-w-10" icon='caret-right' size="lg" />
      </a>
    </b-row>
    <b-collapse :id="`collapse-section-${productType.id}`" v-model="visible">
      <template v-for="(stumpageUnit, index) in productType.stumpageUnits">
        <b-row no-gutters :key="`header${stumpageUnit.id}`" v-if="visible">
          <h5 class="bypass">
            {{productType.name}} (in {{stumpageUnit.name}})
            <FontAwesomeIcon
              focusable="true"
              tabindex="0"
              class="mx-2 text-primary align-self-center"
              icon="info-circle"
              size="sm"
              :id="`sale-type-popover-${productType.id}-${stumpageUnit.id}`"
            />
            <b-popover
              :target="`sale-type-popover-${productType.id}-${stumpageUnit.id}`"
              :triggers="['hover', 'focus']"
              placement="right"
            >
              <p>{{productType.description}}</p>
              <div v-if="stumpageUnit.description">{{stumpageUnit.description}}</div>
            </b-popover>
          </h5>
          <a
            v-if="index === 0"
            v-b-toggle="`collapse-section-${productType.id}`"
            class="ml-4 collapse-icon c-pointer"
          >
            <font-awesome-icon class="when-opened" icon='caret-down' size="lg" />
            <font-awesome-icon class="when-closed fa-w-10" icon='caret-right' size="lg" />
          </a>
        </b-row>
        <b-form-row
          v-if="anyStumpageRow(productType.id, stumpageUnit.id)"
          :key="`labels${stumpageUnit.id}`"
          class="d-none d-lg-flex mb-1"
        >
          <b-col cols="12" :lg="productType.specialtyProductTypeRequired ? 3 : 4" v-if="productType.specialtyProductTypeRequired">
            Product Type
          </b-col>
          <b-col cols="12" :lg="productType.specialtyProductTypeRequired ? 2 : 3">
            Species/Group
          </b-col>
          <b-col cols="12" :lg="productType.specialtyProductTypeRequired ? 3 : 4">
            Volume (in {{stumpageUnit.name}})
          </b-col>
          <b-col cols="12" :lg="productType.specialtyProductTypeRequired ? 3 : 4">
            Price per {{stumpageUnit.priceUnitName}}
          </b-col>
          <b-col cols="12" :lg="1"></b-col>
        </b-form-row>
        <template v-for="(row, index) in stumpages" >
          <template v-if="row.productTypeId === productType.id && row.stumpageUnitId === stumpageUnit.id && anyStumpageRow(productType.id, stumpageUnit.id)">
            <hr :key="`row${row.id || row.key}unit${stumpageUnit.id}hr`" v-if="stumpCount(productType.id, stumpageUnit.id) > 1" class="d-lg-none" />
            <b-form-row
              :key="`row${row.id || row.key}unit${stumpageUnit.id}`"
              class="mb-2"
            >
              <b-col
                v-if="productType.specialtyProductTypeRequired"
                cols="12" :lg="productType.specialtyProductTypeRequired ? 3 : 4"
                class="mb-2 mb-lg-0"
              >
                <ValidationProvider
                  :name="`Product Type ${index}`"
                  rules="required"
                  v-slot="{ errors, classes }"
                  slim
                >
                  <b-form-group
                    class="mb-0"
                    label-cols="12"
                    :label-for="`product-type-${index}`"
                    label-class="d-lg-none"
                  >
                    <template v-slot:label>Product Type</template>
                    <select
                      :id="`product-type-${index}`"
                      :name="`product-type-${index}`"
                      v-model="row.specialtyProductTypeId"
                      class="form-control"
                      :class="classes"
                      :aria-describedby="$errorsIdString(errors, `product-type-${index}`)"
                    >
                      <option :value="null">Select a Product Type</option>
                      <option
                        v-for="option in specialtyProductTypes"
                        :key="option.id"
                        :value="option.id"
                      >
                        {{option.name}}
                      </option>
                    </select>
                    <InputErrors :errors="errors" :errorsIdPrefix="`product-type-${index}`" />
                  </b-form-group>
                </ValidationProvider>
              </b-col>
              <b-col
                cols="12"
                :lg="productType.specialtyProductTypeRequired ? 2 : 3"
                class="mb-2 mb-lg-0"
              >
                <ValidationProvider
                  :name="`Species/Group ${index}`"
                  rules="required"
                  v-slot="{ errors, classes }"
                  slim
                >
                  <b-form-group
                    class="mb-0"
                    label-cols="12"
                    :label-for="`species-${index}`"
                    label-class="d-lg-none"
                  >
                    <template v-slot:label>Species/Group</template>
                    <select
                      :id="`species-${index}`"
                      :name="`species-${index}`"
                      v-model="row.speciesTypeId"
                      class="form-control"
                      :class="classes"
                      :aria-describedby="$errorsIdString(errors, `species-${index}`)"
                    >
                      <option :value="null">Select a Species/Group</option>
                      <option
                        v-for="option in productType.speciesTypes"
                        :key="option.id"
                        :value="option.id"
                      >
                        {{option.name}}
                      </option>
                    </select>
                    <InputErrors :errors="errors" :errorsIdPrefix="`species-${index}`" />
                  </b-form-group>
                </ValidationProvider>
              </b-col>
              <b-col
                cols="12"
                :lg="productType.specialtyProductTypeRequired ? 3 : 4"
                class="mb-2 mb-lg-0"
              >
                <ValidationProvider
                  :name="`Volume ${index}`"
                  rules="max_decimal:2|min_value:0.01|required"
                  v-slot="{ errors, classes }"
                  slim
                >
                  <b-form-group
                    class="mb-0"
                    label-cols="12"
                    :label-for="`volume-${index}`"
                    label-class="d-lg-none"
                  >
                    <template v-slot:label>Volume (in {{stumpageUnit.name}})</template>
                    <b-form-input
                      type="number"
                      :id="`volume-${index}`"
                      :name="`volume-${index}`"
                      v-model="row.volume"
                      placeholder="Enter Volume"
                      :class="classes"
                      maxlength="7"
                      :aria-describedby="$errorsIdString(errors, `volume-${index}`)"
                    />
                    <InputErrors :errors="errors" :errorsIdPrefix="`volume-${index}`" />
                  </b-form-group>
                </ValidationProvider>
              </b-col>
              <b-col
                cols="12"
                :lg="productType.specialtyProductTypeRequired ? 3 : 4"
                class="mb-2 mb-lg-0"
              >
                <ValidationProvider
                  :name="`Price ${index}`"
                  rules="required|money|min_value:0.1"
                  v-slot="{ errors, classes }"
                  slim
                >
                  <b-form-group
                    class="mb-0"
                    label-cols="12"
                    :label-for="`price-${index}`"
                    label-class="d-lg-none"
                  >
                    <template v-slot:label>Price per {{stumpageUnit.priceUnitName}}</template>
                    <b-input-group>
                      <b-input-group-prepend>
                        <b-input-group-text>$</b-input-group-text>
                      </b-input-group-prepend>
                      <b-form-input
                        type="number"
                        :id="`price-${index}`"
                        :name="`price-${index}`"
                        v-model="row.price"
                        placeholder="Enter Price"
                        :class="classes"
                        maxlength="7"
                        :aria-describedby="$errorsIdString(errors, `price-${index}`)"
                      />
                    </b-input-group>
                    <InputErrors
                      :errors="errors"
                      :errorsIdPrefix="`price-${index}`"
                      class="d-block invalid-feedback"
                    />
                  </b-form-group>
                </ValidationProvider>
              </b-col>
              <div class="col-lg-1">
                <label class="col-12 col-form-label invisible d-none text-nowrap">Remove</label>
                <b-button
                  variant="danger"
                  @click="removeRow(index)"
                  class="form-control-height w-100 w-lg-auto"
                  aria-label="Remove Stumpage Row"
                >
                  <FontAwesomeIcon class="align-self-center" icon="trash-alt" />
                </b-button>
              </div>
            </b-form-row>
          </template>
        </template>
        <span
          v-if="!anyStumpageRow(productType.id, stumpageUnit.id)"
          :key="`none${stumpageUnit.id}`"
        >
          None added
        </span>
        <div :key="`add${stumpageUnit.id}`" class="text-right mt-2">
          <a class="form-action-text text-secondary"
            @click="addRow(stumpageUnit.id)"
          >
            + Add Row
          </a>
        </div>
      </template>
    </b-collapse>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import LookupModules from '@/store/moduleConfig';

export default {
  name: 'StumpageSection',
  props: {
    productType: {
      type: Object,
      required: true,
    },
    stumpages: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      visible: true,
    };
  },
  created() {
    this.$store.dispatch(`${LookupModules.SPECIALTY_PRODUCT_TYPES.namespace}/fetchOptions`);
  },
  computed: {
    ...mapState(LookupModules.SPECIALTY_PRODUCT_TYPES.namespace, {
      specialtyProductTypes: state => state.options,
      specialtyProductTypesLoading: state => state.loading,
    }),
  },
  methods: {
    anyStumpageRow(productTypeId, stumpageUnitId) {
      const foundRow = this.stumpages.find(row => row.productTypeId === productTypeId && row.stumpageUnitId === stumpageUnitId);
      return foundRow;
    },
    stumpCount(productTypeId, stumpageUnitId) {
      const rows = this.stumpages.filter(row => row.productTypeId === productTypeId && row.stumpageUnitId === stumpageUnitId);
      return rows.length;
    },
    addRow(stumpageUnitId) {
      this.stumpages.push({
        productTypeId: this.productType.id,
        speciesTypeId: null,
        stumpageUnitId,
        volume: null,
        price: null,
        specialtyProductTypeId: null,
        key: this.stumpages.length,
      });
    },
    removeRow(index) {
      this.stumpages.splice(index, 1);
    },
  },
};
</script>
